import { useEffect, useMemo, useState } from "react";
import { api, idr } from "../lib/api";
import {
  Plus, Trash2, Save, Warehouse, Percent, Search, Info, Trophy, MessageSquareQuote,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import LoadingState from "@/components/ui/loading-state";

const blankGudangRow = () => ({ warehouse_id: "", warehouse_nama: "", poin: 0 });
const blankTierRow = () => ({ min_pct: 0, max_pct: null, cair_pct: 0 });

// Kunci template rumus bawaan. Teksnya "standar_zoa" SENGAJA dipertahankan walau
// labelnya sudah netral ("Standar Bawaan", keputusan owner 24 Sep 2026 white-label):
// kunci ini tersimpan di dokumen config produksi (`rumus_insentif.nama_template`)
// dan dicocokkan dengan RUMUS_TEMPLATES di routers/poin.py — menggantinya membuat
// konfigurasi lama menunjuk template yang tidak ada. Pengguna tidak pernah melihatnya.
const KUNCI_TEMPLATE_BAWAAN = "standar_zoa";

const defaultCfg = () => ({
  aturan_gudang: [],
  poin_akuisisi_per_item: 10,
  poin_default_tanpa_gudang: 5,
  peserta_podium: [],
  tampilkan_omzet_podium: false,
  rumus_insentif: { nama_template: KUNCI_TEMPLATE_BAWAAN, template: [blankTierRow()] },
  kata_motivasi: [],
});

// Replika PERSIS `hitung_insentif` backend (routers/poin.py) — HANYA untuk
// pratinjau di layar ini. Sumber kebenaran tetap backend (slip gaji &
// podium memanggil mesin Python-nya sendiri).
function hitungInsentifPratinjau(realisasiPoin, targetPoin, bobot, tiers) {
  const target = Number(targetPoin) || 0;
  const b = Number(bobot) || 0;
  const realisasi = Number(realisasiPoin) || 0;
  const insentifDasar = Math.round(target * b * 100) / 100;
  const pctRealisasi = target ? Math.round((realisasi / target) * 100 * 100) / 100 : 0;
  const ordered = [...(tiers || [])].sort((a, b2) => (Number(a.min_pct) || 0) - (Number(b2.min_pct) || 0));
  let tierCairPct = 0;
  let tierTerpakai = null;
  for (const t of ordered) {
    const mn = Number(t.min_pct) || 0;
    const mx = t.max_pct === null || t.max_pct === undefined || t.max_pct === "" ? null : Number(t.max_pct);
    if (pctRealisasi >= mn && (mx === null || pctRealisasi < mx)) {
      tierCairPct = Number(t.cair_pct) || 0;
      tierTerpakai = { min_pct: mn, max_pct: mx, cair_pct: tierCairPct };
      break;
    }
  }
  const insentifCair = Math.round(((insentifDasar * tierCairPct) / 100) * 100) / 100;
  return { insentifDasar, pctRealisasi, tierCairPct, insentifCair, tierTerpakai };
}

export default function PoinKonfigurasi() {
  const { can } = useAuth();
  const canEdit = can("sales_targets", "update");

  const [cfg, setCfg] = useState(defaultCfg());
  const [gudangTersedia, setGudangTersedia] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [emps, setEmps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Pemilih peserta podium — cari + saring divisi (pola KpiTargets.jsx)
  const [pesertaSearch, setPesertaSearch] = useState("");
  const [pesertaDivisiFilter, setPesertaDivisiFilter] = useState("");

  // Pratinjau rumus insentif
  const [prevTarget, setPrevTarget] = useState(100);
  const [prevRealisasi, setPrevRealisasi] = useState(100);
  const [prevBobot, setPrevBobot] = useState(10000);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/poin/config"),
      api.get("/poin/gudang-tersedia"),
      api.get("/poin/rumus-templates"),
      api.get("/employees"),
    ]).then(([cfgR, gudangR, tplR, empR]) => {
      const c = cfgR.data || defaultCfg();
      setCfg({
        aturan_gudang: c.aturan_gudang || [],
        poin_akuisisi_per_item: c.poin_akuisisi_per_item ?? 10,
        poin_default_tanpa_gudang: c.poin_default_tanpa_gudang ?? 5,
        peserta_podium: c.peserta_podium || [],
        tampilkan_omzet_podium: !!c.tampilkan_omzet_podium,
        rumus_insentif: c.rumus_insentif || { nama_template: KUNCI_TEMPLATE_BAWAAN, template: [blankTierRow()] },
        kata_motivasi: c.kata_motivasi || [],
      });
      setGudangTersedia(gudangR.data?.items || []);
      setTemplates(tplR.data?.items || []);
      setEmps((empR.data || []).filter(e => e.is_active !== false));
    }).catch(e => toast.error(e?.response?.data?.detail || "Gagal memuat konfigurasi poin"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  // ---------- (a) Aturan poin per gudang ----------
  const addGudangRow = () => setCfg(s => ({ ...s, aturan_gudang: [...s.aturan_gudang, blankGudangRow()] }));
  const removeGudangRow = (idx) => setCfg(s => ({ ...s, aturan_gudang: s.aturan_gudang.filter((_, i) => i !== idx) }));
  const updGudangRow = (idx, patch) => setCfg(s => ({
    ...s, aturan_gudang: s.aturan_gudang.map((g, i) => i === idx ? { ...g, ...patch } : g),
  }));
  const gudangOptionsFor = (idx) => {
    const usedElsewhere = new Set(cfg.aturan_gudang.filter((_, i) => i !== idx).map(g => g.warehouse_id).filter(Boolean));
    return gudangTersedia.filter(g => !usedElsewhere.has(g.id));
  };

  // ---------- (b) Peserta podium ----------
  const divisiOptions = useMemo(() => [...new Set(emps.map(e => e.divisi).filter(Boolean))].sort(), [emps]);
  const pesertaFilteredEmps = useMemo(() => emps.filter(e => {
    if (pesertaSearch && !(e.nama || "").toLowerCase().includes(pesertaSearch.toLowerCase())) return false;
    if (pesertaDivisiFilter && e.divisi !== pesertaDivisiFilter) return false;
    return true;
  }), [emps, pesertaSearch, pesertaDivisiFilter]);
  const togglePeserta = (id) => setCfg(s => ({
    ...s, peserta_podium: s.peserta_podium.includes(id)
      ? s.peserta_podium.filter(x => x !== id) : [...s.peserta_podium, id],
  }));
  const selectAllVisiblePeserta = () => setCfg(s => ({
    ...s, peserta_podium: [...new Set([...s.peserta_podium, ...pesertaFilteredEmps.map(e => e.id)])],
  }));
  const clearPeserta = () => setCfg(s => ({ ...s, peserta_podium: [] }));

  // ---------- (c) Rumus insentif ----------
  const applyRumusTemplate = (key) => {
    const tpl = templates.find(t => t.key === key);
    if (!tpl) return;
    setCfg(s => ({ ...s, rumus_insentif: { nama_template: key, template: tpl.template.map(t => ({ ...t })) } }));
    toast.success(`Template rumus "${tpl.label}" diterapkan`);
  };
  const addTierRow = () => setCfg(s => ({
    ...s,
    rumus_insentif: { nama_template: "custom", template: [...s.rumus_insentif.template, blankTierRow()] },
  }));
  const removeTierRow = (idx) => setCfg(s => ({
    ...s,
    rumus_insentif: {
      nama_template: "custom",
      template: s.rumus_insentif.template.filter((_, i) => i !== idx),
    },
  }));
  const updTierRow = (idx, patch) => setCfg(s => ({
    ...s,
    rumus_insentif: {
      nama_template: "custom",
      template: s.rumus_insentif.template.map((t, i) => i === idx ? { ...t, ...patch } : t),
    },
  }));

  const previewHasil = useMemo(
    () => hitungInsentifPratinjau(prevRealisasi, prevTarget, prevBobot, cfg.rumus_insentif.template),
    [prevRealisasi, prevTarget, prevBobot, cfg.rumus_insentif.template]
  );

  // ---------- Simpan ----------
  const save = async () => {
    const aturan = cfg.aturan_gudang
      .filter(g => g.warehouse_id)
      .map(g => ({ warehouse_id: g.warehouse_id, warehouse_nama: g.warehouse_nama || "", poin: Number(g.poin) || 0 }));
    const tiers = cfg.rumus_insentif.template.map(t => ({
      min_pct: Number(t.min_pct) || 0,
      max_pct: (t.max_pct === null || t.max_pct === undefined || t.max_pct === "") ? null : Number(t.max_pct),
      cair_pct: Number(t.cair_pct) || 0,
    }));
    if (tiers.length === 0) { toast.error("Rumus insentif butuh minimal 1 tier"); return; }
    const kataMotivasi = cfg.kata_motivasi.map(k => k.trim()).filter(Boolean);
    setSaving(true);
    try {
      const payload = {
        aturan_gudang: aturan,
        poin_akuisisi_per_item: Number(cfg.poin_akuisisi_per_item) || 0,
        poin_default_tanpa_gudang: Number(cfg.poin_default_tanpa_gudang) || 0,
        peserta_podium: cfg.peserta_podium,
        tampilkan_omzet_podium: cfg.tampilkan_omzet_podium,
        rumus_insentif: { nama_template: cfg.rumus_insentif.nama_template || "custom", template: tiers },
        kata_motivasi: kataMotivasi,
      };
      const { data } = await api.put("/poin/config", payload);
      setCfg({
        aturan_gudang: data.aturan_gudang || [],
        poin_akuisisi_per_item: data.poin_akuisisi_per_item ?? 10,
        poin_default_tanpa_gudang: data.poin_default_tanpa_gudang ?? 5,
        peserta_podium: data.peserta_podium || [],
        tampilkan_omzet_podium: !!data.tampilkan_omzet_podium,
        rumus_insentif: data.rumus_insentif || { nama_template: KUNCI_TEMPLATE_BAWAAN, template: [blankTierRow()] },
        kata_motivasi: data.kata_motivasi || [],
      });
      toast.success("Konfigurasi poin disimpan");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan konfigurasi poin");
    }
    setSaving(false);
  };

  if (loading) return <div className="p-4 md:p-8 max-w-[1200px]"><LoadingState loading data-testid="poin-konfigurasi-loading" /></div>;

  return (
    <div className="p-4 md:p-8 max-w-[1200px]" data-testid="poin-konfigurasi-page">
      <header className="flex items-start justify-between mb-6 md:mb-8 flex-wrap gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-2">Pengaturan</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Konfigurasi Poin &amp; Insentif</h1>
          <p className="mt-2 text-sm text-slate-500 max-w-2xl">
            Atur poin per gudang, peserta podium, dan rumus insentif berjenjang. Perubahan berlaku untuk Podium Sales &amp; Target vs Realisasi berikutnya.
          </p>
        </div>
        {canEdit && (
          <button onClick={save} disabled={saving} data-testid="poin-simpan"
            className="h-10 px-5 rounded-lg bg-[#059669] hover:bg-[#047857] text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60">
            <Save className="w-4 h-4" /> {saving ? "Menyimpan..." : "Simpan Konfigurasi"}
          </button>
        )}
      </header>

      {!canEdit && (
        <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Anda hanya bisa melihat konfigurasi ini. Hubungi admin untuk melakukan perubahan.
        </div>
      )}

      {/* (a) Aturan Poin per Gudang */}
      <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 mb-6">
        <div className="flex items-center gap-2 mb-1">
          <Warehouse className="w-4 h-4 text-emerald-600" />
          <h2 className="font-display text-base font-bold text-slate-900">Aturan Poin per Gudang</h2>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          Poin penjualan mengikuti gudang unit fisik (ditelusuri dari IMEI). Contoh: <b>10</b> = gudang utama, <b>5</b> = Acc, <b>15</b> = Second.
        </p>

        <div className="border border-slate-200 rounded-lg overflow-hidden mb-4">
          <div className="bg-slate-50 px-3 py-2 grid grid-cols-12 gap-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
            <div className="col-span-7">Gudang</div>
            <div className="col-span-3 text-right">Poin</div>
            <div className="col-span-2" />
          </div>
          <div className="divide-y divide-slate-100">
            {cfg.aturan_gudang.length === 0 && (
              <p className="p-4 text-xs text-slate-400 italic text-center">Belum ada aturan gudang. Selama kosong, poin memakai skema lama (HP=10, ACC=5).</p>
            )}
            {cfg.aturan_gudang.map((g, idx) => (
              <div key={idx} className="p-2.5 grid grid-cols-12 gap-2 items-center" data-testid={`poin-gudang-row-${idx}`}>
                <select value={g.warehouse_id} disabled={!canEdit}
                  onChange={e => {
                    const wh = gudangTersedia.find(x => x.id === e.target.value);
                    updGudangRow(idx, { warehouse_id: e.target.value, warehouse_nama: wh?.nama || "" });
                  }}
                  data-testid={`poin-gudang-pilih-${idx}`}
                  className="col-span-7 h-9 px-2 rounded border border-slate-200 text-xs bg-white disabled:bg-slate-50">
                  <option value="">— pilih gudang —</option>
                  {gudangOptionsFor(idx).map(gu => <option key={gu.id} value={gu.id}>{gu.nama}</option>)}
                  {g.warehouse_id && !gudangTersedia.some(gu => gu.id === g.warehouse_id) && (
                    <option value={g.warehouse_id}>{g.warehouse_nama || g.warehouse_id} (tidak aktif)</option>
                  )}
                </select>
                <input type="number" value={g.poin} disabled={!canEdit}
                  onChange={e => updGudangRow(idx, { poin: e.target.value })}
                  data-testid={`poin-gudang-poin-${idx}`}
                  className="col-span-3 h-9 px-2 rounded border border-slate-200 text-xs text-right font-mono disabled:bg-slate-50" />
                <div className="col-span-2 flex justify-end">
                  {canEdit && (
                    <button onClick={() => removeGudangRow(idx)} data-testid={`poin-gudang-hapus-${idx}`}
                      className="h-9 w-9 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-600 inline-grid place-items-center">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {canEdit && (
          <button onClick={addGudangRow} data-testid="poin-gudang-tambah"
            className="mb-4 text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-medium">
            <Plus className="w-3.5 h-3.5" /> Tambah Baris Gudang
          </button>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Poin Akuisisi per Item</span>
            <input type="number" value={cfg.poin_akuisisi_per_item} disabled={!canEdit}
              onChange={e => setCfg(s => ({ ...s, poin_akuisisi_per_item: e.target.value }))}
              data-testid="poin-akuisisi-per-item"
              placeholder="Contoh: 10"
              className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm font-mono disabled:bg-slate-50" />
            <span className="text-[11px] text-slate-400">Diberikan flat per unit akuisisi, apa pun kode barangnya. Default 10.</span>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Poin Default Tanpa Gudang</span>
            <input type="number" value={cfg.poin_default_tanpa_gudang} disabled={!canEdit}
              onChange={e => setCfg(s => ({ ...s, poin_default_tanpa_gudang: e.target.value }))}
              data-testid="poin-default-tanpa-gudang"
              placeholder="Contoh: 5"
              className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm font-mono disabled:bg-slate-50" />
            <span className="text-[11px] text-slate-400">Dipakai kalau IMEI tidak ditemukan / gudangnya belum diatur di atas.</span>
          </label>
        </div>
      </section>

      {/* (b) Peserta Podium */}
      <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 mb-6">
        <div className="flex items-center gap-2 mb-1">
          <Trophy className="w-4 h-4 text-emerald-600" />
          <h2 className="font-display text-base font-bold text-slate-900">Peserta Podium</h2>
        </div>
        <p className="text-xs text-slate-500 mb-4">Pilih karyawan yang ikut kompetisi Podium Sales. Kosongkan semua = semua karyawan aktif ikut.</p>

        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <div className="p-3 space-y-2.5 bg-slate-50/60">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[180px]">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input value={pesertaSearch} onChange={e => setPesertaSearch(e.target.value)}
                  data-testid="poin-peserta-search" placeholder="Cari nama karyawan..." disabled={!canEdit}
                  className="h-9 pl-8 pr-3 rounded-lg border border-slate-200 text-xs w-full bg-white disabled:bg-slate-50" />
              </div>
              {divisiOptions.length > 0 && (
                <select value={pesertaDivisiFilter} onChange={e => setPesertaDivisiFilter(e.target.value)}
                  data-testid="poin-peserta-filter-divisi"
                  className="h-9 px-2 rounded-lg border border-slate-200 text-xs bg-white">
                  <option value="">Semua Divisi</option>
                  {divisiOptions.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              )}
            </div>
            {canEdit && (
              <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                <div className="flex gap-2">
                  <button type="button" onClick={selectAllVisiblePeserta} data-testid="poin-peserta-pilih-semua"
                    className="px-2 py-1 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50 font-medium">
                    Pilih semua yang tampil ({pesertaFilteredEmps.length})
                  </button>
                  <button type="button" onClick={clearPeserta} data-testid="poin-peserta-kosongkan"
                    className="px-2 py-1 rounded border border-slate-200 text-slate-600 hover:bg-slate-50 font-medium">
                    Kosongkan (semua ikut)
                  </button>
                </div>
                <span className="font-semibold text-emerald-700" data-testid="poin-peserta-selected-count">
                  {cfg.peserta_podium.length === 0 ? "Semua karyawan ikut" : `${cfg.peserta_podium.length} karyawan dipilih`}
                </span>
              </div>
            )}
          </div>
          <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
            {pesertaFilteredEmps.length === 0 ? (
              <p className="p-3 text-xs text-slate-400 italic text-center">Tidak ada karyawan yang cocok</p>
            ) : pesertaFilteredEmps.map(e => (
              <label key={e.id} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer text-xs">
                <input type="checkbox" checked={cfg.peserta_podium.includes(e.id)} disabled={!canEdit}
                  onChange={() => togglePeserta(e.id)}
                  data-testid={`poin-peserta-${e.id}`}
                  className="w-3.5 h-3.5 accent-emerald-600" />
                <span className="flex-1 text-slate-800">{e.nama}</span>
                <span className="text-slate-400 truncate max-w-[45%]">{[e.divisi, e.branch].filter(Boolean).join(" · ") || "-"}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
          <div>
            <p className="text-sm font-medium text-slate-800">Tampilkan Omzet di Podium</p>
            <p className="text-[11px] text-slate-500">Saat mati, podium hanya menampilkan poin saja (nama, cabang, peringkat) tanpa omzet/transaksi.</p>
          </div>
          <button type="button" role="switch" aria-checked={cfg.tampilkan_omzet_podium} disabled={!canEdit}
            onClick={() => setCfg(s => ({ ...s, tampilkan_omzet_podium: !s.tampilkan_omzet_podium }))}
            data-testid="poin-toggle-omzet-podium"
            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-60 ${
              cfg.tampilkan_omzet_podium ? "bg-emerald-600" : "bg-slate-300"
            }`}>
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              cfg.tampilkan_omzet_podium ? "translate-x-6" : "translate-x-1"
            }`} />
          </button>
        </div>
      </section>

      {/* (c) Rumus Insentif */}
      <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 mb-6">
        <div className="flex items-center gap-2 mb-1">
          <Percent className="w-4 h-4 text-emerald-600" />
          <h2 className="font-display text-base font-bold text-slate-900">Rumus Insentif Berjenjang</h2>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          insentif_dasar = target poin × bobot per poin. Cair sebagian sesuai tier pencapaian di bawah.
        </p>

        {canEdit && (
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="text-xs font-semibold text-slate-700">Pakai Template:</span>
            <select onChange={e => e.target.value && applyRumusTemplate(e.target.value)} value=""
              data-testid="poin-rumus-pilih"
              className="h-9 px-2 rounded-lg border border-slate-300 text-xs bg-white flex-1 min-w-[240px]">
              <option value="">— pilih template rumus —</option>
              {templates.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>
        )}

        <div className="border border-slate-200 rounded-lg overflow-hidden mb-2">
          <div className="bg-slate-50 px-3 py-2 grid grid-cols-12 gap-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
            <div className="col-span-4">Min % Realisasi</div>
            <div className="col-span-4">Max % Realisasi</div>
            <div className="col-span-3 text-right">% Cair</div>
            <div className="col-span-1" />
          </div>
          <div className="divide-y divide-slate-100">
            {cfg.rumus_insentif.template.map((t, idx) => (
              <div key={idx} className="p-2.5 grid grid-cols-12 gap-2 items-center" data-testid={`poin-tier-row-${idx}`}>
                <input type="number" value={t.min_pct} disabled={!canEdit}
                  onChange={e => updTierRow(idx, { min_pct: e.target.value })}
                  data-testid={`poin-tier-min-${idx}`}
                  className="col-span-4 h-9 px-2 rounded border border-slate-200 text-xs text-right font-mono disabled:bg-slate-50" />
                <input type="number" value={t.max_pct === null || t.max_pct === undefined ? "" : t.max_pct}
                  placeholder="tanpa batas" disabled={!canEdit}
                  onChange={e => updTierRow(idx, { max_pct: e.target.value === "" ? null : e.target.value })}
                  data-testid={`poin-tier-max-${idx}`}
                  className="col-span-4 h-9 px-2 rounded border border-slate-200 text-xs text-right font-mono disabled:bg-slate-50" />
                <input type="number" value={t.cair_pct} disabled={!canEdit}
                  onChange={e => updTierRow(idx, { cair_pct: e.target.value })}
                  data-testid={`poin-tier-cair-${idx}`}
                  className="col-span-3 h-9 px-2 rounded border border-slate-200 text-xs text-right font-mono disabled:bg-slate-50" />
                <div className="col-span-1 flex justify-end">
                  {canEdit && (
                    <button onClick={() => removeTierRow(idx)} data-testid={`poin-tier-hapus-${idx}`}
                      className="h-9 w-9 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-600 inline-grid place-items-center">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        <p className="text-[11px] text-slate-400 mb-4">Kosongkan Max % pada tier tertinggi = tanpa batas atas. Tier tidak boleh tumpang tindih (divalidasi saat simpan).</p>

        {canEdit && (
          <button onClick={addTierRow} data-testid="poin-tier-tambah"
            className="mb-6 text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-medium">
            <Plus className="w-3.5 h-3.5" /> Tambah Tier
          </button>
        )}

        {/* Pratinjau hidup */}
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-4">
          <p className="text-xs font-semibold text-emerald-800 mb-3 inline-flex items-center gap-1.5">
            <Info className="w-3.5 h-3.5" /> Pratinjau Perhitungan (contoh — angka resmi tetap dari slip gaji)
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <label className="block">
              <span className="text-[11px] font-medium text-slate-600">Target Poin</span>
              <input type="number" value={prevTarget} onChange={e => setPrevTarget(e.target.value)}
                data-testid="poin-preview-target"
                className="mt-1 w-full h-9 px-2 rounded border border-slate-200 text-xs text-right font-mono bg-white" />
            </label>
            <label className="block">
              <span className="text-[11px] font-medium text-slate-600">Realisasi Poin</span>
              <input type="number" value={prevRealisasi} onChange={e => setPrevRealisasi(e.target.value)}
                data-testid="poin-preview-realisasi"
                className="mt-1 w-full h-9 px-2 rounded border border-slate-200 text-xs text-right font-mono bg-white" />
            </label>
            <label className="block">
              <span className="text-[11px] font-medium text-slate-600">Bobot / Poin (Rp)</span>
              <input type="number" value={prevBobot} onChange={e => setPrevBobot(e.target.value)}
                data-testid="poin-preview-bobot"
                className="mt-1 w-full h-9 px-2 rounded border border-slate-200 text-xs text-right font-mono bg-white" />
            </label>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <PreviewStat label="% Realisasi" value={`${previewHasil.pctRealisasi}%`} />
            <PreviewStat label="Tier Cair" value={`${previewHasil.tierCairPct}%`} />
            <PreviewStat label="Insentif Dasar" value={idr(previewHasil.insentifDasar)} />
            <PreviewStat label="Insentif Cair" value={idr(previewHasil.insentifCair)} highlight />
          </div>
        </div>
      </section>

      {/* (d) Kata Motivasi Podium */}
      <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 mb-6">
        <div className="flex items-center gap-2 mb-1">
          <MessageSquareQuote className="w-4 h-4 text-emerald-600" />
          <h2 className="font-display text-base font-bold text-slate-900">Kata Motivasi Podium</h2>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          Kalimat penyemangat yang tampil di bagian bawah Podium Sales, satu kalimat per baris. Kalau diisi
          lebih dari satu, kalimat tampil bergantian setiap podium auto-refresh. Kosongkan semua = footer tidak menampilkan apa pun.
        </p>
        <textarea
          value={cfg.kata_motivasi.join("\n")}
          disabled={!canEdit}
          onChange={e => setCfg(s => ({ ...s, kata_motivasi: e.target.value.split("\n") }))}
          data-testid="poin-kata-motivasi"
          rows={5}
          placeholder={"Contoh:\nSemangat terus, target menanti!\nSetiap transaksi adalah langkah menuju juara."}
          className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm disabled:bg-slate-50"
        />
      </section>
    </div>
  );
}

function PreviewStat({ label, value, highlight }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-2.5">
      <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">{label}</p>
      <p className={`font-mono font-bold ${highlight ? "text-emerald-700 text-sm" : "text-slate-900 text-sm"}`}>{value}</p>
    </div>
  );
}
